/**
 * 깊이맵 + 실루엣 → 실제 메시.
 *
 * 두 가지 모드가 있다.
 *
 * **relief** — 격자를 깊이만큼 밀어낸 한 겹. 부조(浮彫). 배경이 있는 사진,
 * 지형, 벽 텍스처처럼 "정면에서만 보면 되는" 것에 쓴다.
 *
 * **inflate** — 앞 껍질과 뒤 껍질을 실루엣에서 꿰매 닫은 입체. 이게 캐릭터와
 * 몬스터를 위한 모드다. 앞면 형상은 깊이맵이, 두께는 실루엣 거리장이 만든다.
 * 한 장의 그림에서 나올 수 있는 최선은 "속을 채워 부풀린 인형"이고, 이 모드가
 * 정확히 그것을 만든다. 뒤통수를 지어내지 않는 대신 거짓말도 하지 않는다.
 *
 * 이음매 처리가 이 파일의 전부다. 앞뒤 껍질을 실루엣에서 **얇은 옆면 띠**로 잇는다.
 * 정점을 그냥 공유해 z=0에 붙이면 폭이 한 칸뿐인 부위에서 앞뒤 삼각형이 겹쳐
 * non-manifold가 된다 — 화면에서는 멀쩡해 보이고 엔진에 넣으면 드러난다.
 * `test/pipeline.test.ts`가 유향 간선을 세어 이걸 지킨다.
 */

import * as THREE from 'three';
import type { Field, ForgeParams, ImageAnalysis } from '../types.ts';
import { clamp, sampleField } from '../imaging/raster.ts';

export interface ForgedMesh {
  geometry: THREE.BufferGeometry;
  vertexCount: number;
  triangleCount: number;
  /** 이미지 좌표계에서 잘라낸 피사체 영역. UV 계산과 디버깅에 쓴다. */
  bounds: { x0: number; y0: number; x1: number; y1: number };
}

/** 마스크가 실제로 차 있는 사각형. 여백에 정점을 낭비하지 않기 위해. */
function subjectBounds(mask: Field, pad = 0.02) {
  const { width: w, height: h } = mask;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask.data[y * w + x]! < 0.5) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return { x0: 0, y0: 0, x1: 1, y1: 1 };
  const px = (maxX - minX) * pad;
  const py = (maxY - minY) * pad;
  return {
    x0: clamp((minX - px) / (w - 1)),
    y0: clamp((minY - py) / (h - 1)),
    x1: clamp((maxX + px) / (w - 1)),
    y1: clamp((maxY + py) / (h - 1)),
  };
}

/**
 * 격자 위 높이값 라플라시안 스무딩.
 * 경계(z=0으로 고정된 점)와 바깥은 건드리지 않는다 — 건드리면 실루엣이 녹는다.
 */
function relaxHeights(
  z: Float32Array,
  inside: Uint8Array,
  pinned: Uint8Array,
  cols: number,
  rows: number,
  iterations: number,
): void {
  if (iterations <= 0) return;
  const next = new Float32Array(z.length);
  for (let it = 0; it < iterations; it++) {
    next.set(z);
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const idx = j * cols + i;
        if (!inside[idx] || pinned[idx]) continue;
        let sum = 0;
        let n = 0;
        if (i > 0 && inside[idx - 1]) { sum += z[idx - 1]!; n++; }
        if (i < cols - 1 && inside[idx + 1]) { sum += z[idx + 1]!; n++; }
        if (j > 0 && inside[idx - cols]) { sum += z[idx - cols]!; n++; }
        if (j < rows - 1 && inside[idx + cols]) { sum += z[idx + cols]!; n++; }
        if (n === 0) continue;
        next[idx] = z[idx]! * 0.35 + (sum / n) * 0.65;
      }
    }
    z.set(next);
  }
}

export function buildMesh(
  analysis: ImageAnalysis,
  depth: Field,
  params: ForgeParams,
): ForgedMesh {
  const { mask, distance } = analysis;
  const bounds = subjectBounds(mask);
  const spanU = Math.max(1e-4, bounds.x1 - bounds.x0);
  const spanV = Math.max(1e-4, bounds.y1 - bounds.y0);

  const aspect = (spanV * analysis.source.height) / (spanU * analysis.source.width);
  const cols = Math.max(8, Math.round(params.resolution)) + 1;
  const rows = Math.max(8, Math.round(params.resolution * aspect)) + 1;
  const cellCount = cols * rows;

  // 1) 격자점마다 마스크/깊이/거리를 샘플링한다.
  const insideV = new Uint8Array(cellCount);
  const used = new Uint8Array(cellCount);
  const pinned = new Uint8Array(cellCount);
  const uArr = new Float32Array(cellCount);
  const vArr = new Float32Array(cellCount);
  const dtArr = new Float32Array(cellCount);
  const mArr = new Float32Array(cellCount);
  const zFront = new Float32Array(cellCount);
  const zBack = new Float32Array(cellCount);
  /** 실루엣에 붙이기 위한 격자 단위 XY 오프셋. 경계 정점만 0이 아니다. */
  const offX = new Float32Array(cellCount);
  const offY = new Float32Array(cellCount);

  for (let j = 0; j < rows; j++) {
    const ty = j / (rows - 1);
    const v = bounds.y0 + ty * spanV;
    for (let i = 0; i < cols; i++) {
      const tx = i / (cols - 1);
      const u = bounds.x0 + tx * spanU;
      const idx = j * cols + i;
      uArr[idx] = u;
      vArr[idx] = v;
      const m = sampleField(mask, u, v);
      mArr[idx] = m;
      insideV[idx] = m >= 0.5 ? 1 : 0;
      dtArr[idx] = sampleField(distance, u, v);
    }
  }

  /**
   * 2) 셀을 먼저 정하고, 정점의 신분을 셀에서 되읽는다.
   *
   * 여기가 이 파일에서 제일 틀리기 쉬운 곳이다. "안쪽 정점 중 바깥 정점과 맞닿은 것"을
   * 경계로 잡으면 안 된다. 삼각형은 정점이 아니라 **셀** 단위로 나가기 때문에,
   * 네 이웃이 전부 안쪽이면서도 채워진 영역의 가장자리에 놓인 정점이 존재한다
   * (대각선 방향으로만 바깥에 닿는 경우). 그런 정점을 안 꿰매면 앞뒤 껍질 사이에
   * 실금 같은 구멍이 남는다 — 화면에서는 잘 안 보이고 glb를 엔진에 넣으면 드러난다.
   *
   * 정확한 정의: **네 개의 인접 셀이 모두 채워진 정점만 내부**다. 나머지는 전부 경계.
   */
  const cellOn = new Uint8Array(Math.max(1, (cols - 1) * (rows - 1)));
  for (let j = 0; j < rows - 1; j++) {
    for (let i = 0; i < cols - 1; i++) {
      const a = j * cols + i;
      cellOn[j * (cols - 1) + i] =
        insideV[a] && insideV[a + 1] && insideV[a + cols] && insideV[a + cols + 1] ? 1 : 0;
    }
  }

  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const idx = j * cols + i;
      let on = 0;
      for (let dj = -1; dj <= 0; dj++) {
        for (let di = -1; di <= 0; di++) {
          const ci = i + di;
          const cj = j + dj;
          if (ci < 0 || cj < 0 || ci >= cols - 1 || cj >= rows - 1) continue;
          on += cellOn[cj * (cols - 1) + ci]!;
        }
      }
      used[idx] = on > 0 ? 1 : 0;
      pinned[idx] = on > 0 && on < 4 ? 1 : 0;
    }
  }

  /**
   * 2-b) 경계 정점을 실제 실루엣 위로 옮긴다.
   *
   * 격자 정점을 그대로 두면 외곽선이 계단이 된다 — 해상도 128에서 옆에서 보면
   * 톱니가 그대로 보인다. 마스크가 0.5를 지나는 지점을 이웃 방향으로 보간해
   * 정점을 거기까지 밀어 넣는다. 마칭 스퀘어의 절반짜리 버전이고, 삼각형 위상은
   * 건드리지 않으므로 다양체 보장은 그대로 남는다.
   */
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const idx = j * cols + i;
      if (!pinned[idx]) continue;
      const here = mArr[idx]!;
      let sx = 0, sy = 0, n = 0;
      const probe = (nIdx: number, dx: number, dy: number) => {
        const there = mArr[nIdx]!;
        if (there >= 0.5) return;
        const t = (here - 0.5) / Math.max(1e-4, here - there);
        sx += dx * Math.min(0.95, t);
        sy += dy * Math.min(0.95, t);
        n++;
      };
      if (i > 0) probe(idx - 1, -1, 0);
      if (i < cols - 1) probe(idx + 1, 1, 0);
      if (j > 0) probe(idx - cols, 0, -1);
      if (j < rows - 1) probe(idx + cols, 0, 1);
      if (n === 0) continue;
      offX[idx] = sx / n;
      offY[idx] = sy / n;
    }
  }

  /**
   * 3) 높이.
   *
   * 경계는 z=0으로 붙여 앞뒤를 만나게 하는 대신 **얇은 두께**(rimEps)를 준다.
   * 정확히 붙이면 앞뒤 삼각형이 겹쳐 non-manifold가 되고 — 폭이 한 칸뿐인
   * 부위에서 같은 유향 간선이 두 번 나온다 — 엔진에 따라 법선이 뒤집히거나
   * 부울 연산이 깨진다. 옆면을 실제 띠(rim)로 만들고 나면 그 문제가 사라지고,
   * 덤으로 실루엣 가장자리에 빛이 걸릴 면이 생겨 그림이 낫다.
   */
  const isInflate = params.meshMode === 'inflate';
  const rimEps = isInflate ? Math.max(0.004, params.inflate * 0.03) : 0;
  const rimWidth = 0.09;
  for (let idx = 0; idx < cellCount; idx++) {
    if (!used[idx]) continue;
    if (pinned[idx]) {
      zFront[idx] = rimEps;
      zBack[idx] = -rimEps;
      continue;
    }
    const dt = dtArr[idx]!;
    const shape = Math.min(1, dt / rimWidth);
    const d = sampleField(depth, uArr[idx]!, vArr[idx]!);
    zFront[idx] = rimEps + d * params.depthScale * Math.sqrt(shape);
    zBack[idx] = -rimEps - Math.pow(dt, 0.5) * params.inflate;
  }

  relaxHeights(zFront, used, pinned, cols, rows, params.smooth);
  relaxHeights(zBack, used, pinned, cols, rows, params.smooth);

  // 4) 정점 배출. 앞뒤가 각자 정점을 갖고, 옆면 띠가 둘을 잇는다.
  const frontIdx = new Int32Array(cellCount).fill(-1);
  const backIdx = new Int32Array(cellCount).fill(-1);
  const positions: number[] = [];
  const uvs: number[] = [];
  const shells: number[] = [];

  // 월드 크기: 긴 변을 2로. 이미지 종횡비 유지.
  const worldW = aspect >= 1 ? 2 / aspect : 2;
  const worldH = aspect >= 1 ? 2 : 2 * aspect;

  const emit = (idx: number, z: number, shell: number): number => {
    const tx = ((idx % cols) + offX[idx]!) / (cols - 1);
    const ty = (Math.floor(idx / cols) + offY[idx]!) / (rows - 1);
    const vi = positions.length / 3;
    positions.push((tx - 0.5) * worldW, (0.5 - ty) * worldH, z);
    // 옮긴 만큼 UV도 따라가야 텍스처가 실루엣에서 밀리지 않는다.
    const u = bounds.x0 + tx * spanU;
    const v = bounds.y0 + ty * spanV;
    // CanvasTexture는 flipY가 켜져 있으므로 v를 뒤집어 이미지 위쪽이 위로 온다.
    uvs.push(u, 1 - v);
    shells.push(shell);
    return vi;
  };

  for (let idx = 0; idx < cellCount; idx++) {
    if (!used[idx]) continue;
    frontIdx[idx] = emit(idx, zFront[idx]!, 0);
    if (isInflate) backIdx[idx] = emit(idx, zBack[idx]!, 1);
  }

  // 5) 삼각형.
  const indices: number[] = [];
  const cellFilled = (ci: number, cj: number) =>
    ci >= 0 && cj >= 0 && ci < cols - 1 && cj < rows - 1 && cellOn[cj * (cols - 1) + ci] === 1;

  for (let j = 0; j < rows - 1; j++) {
    for (let i = 0; i < cols - 1; i++) {
      if (!cellFilled(i, j)) continue;
      const a = j * cols + i;       // 좌상
      const b = a + 1;              // 우상
      const c = a + cols;           // 좌하
      const d = c + 1;              // 우하

      const fa = frontIdx[a]!, fb = frontIdx[b]!, fc = frontIdx[c]!, fd = frontIdx[d]!;
      // 앞면 둘레는 a→c→d→b→a 방향(반시계, +Z를 향함)이 된다.
      indices.push(fa, fc, fb, fb, fc, fd);

      if (!isInflate) continue;

      const ba = backIdx[a]!, bb = backIdx[b]!, bc = backIdx[c]!, bd = backIdx[d]!;
      // 뒷면은 감기 방향을 뒤집어 바깥(−Z)을 보게 한다.
      indices.push(ba, bb, bc, bb, bd, bc);

      // 옆면 띠. 이웃 셀이 비어 있는 변이 곧 실루엣이다.
      // 앞면이 p→q로 지나간 변이므로, 띠는 반대 방향 q→p를 채워 넣어야 짝이 맞는다.
      const rim = (p: number, q: number) => {
        const fp = frontIdx[p]!, fq = frontIdx[q]!;
        const bp = backIdx[p]!, bq = backIdx[q]!;
        indices.push(fq, fp, bp, fq, bp, bq);
      };
      if (!cellFilled(i - 1, j)) rim(a, c); // 왼쪽
      if (!cellFilled(i, j + 1)) rim(c, d); // 아래
      if (!cellFilled(i + 1, j)) rim(d, b); // 오른쪽
      if (!cellFilled(i, j - 1)) rim(b, a); // 위
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('aShell', new THREE.Float32BufferAttribute(shells, 1));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();

  // 중심을 원점으로. 회전시켰을 때 화면 밖으로 도망가지 않는다.
  const box = geometry.boundingBox;
  if (box) {
    const center = new THREE.Vector3();
    box.getCenter(center);
    geometry.translate(-center.x, -center.y, -center.z);
    geometry.computeBoundingSphere();
  }

  return {
    geometry,
    vertexCount: positions.length / 3,
    triangleCount: indices.length / 3,
    bounds,
  };
}
